/**
  * @class mmodPlayer
  * @description
  * Video player module displays video image from cameras,
  * actually recognised license plates and measured radar records.
  * Module has two options for display - overview and individual video channel.
  * Video module is just a simple wrapper around <mele-player> web element
  * with some basic configuration.
  * Video player includes also options to manage time of playback
  * (either history time or live play).
  * There is also option to select start and end dat for exporting the recorded data.
  * Module is implemented without workflow, as a pure module named mmodPlayer.
  * Video module receives data from msysStreamer service after invoking mmPlay message.
  * Streaming services provided by msysStreamer are hosted on mBox application
  * running on in-vehicle computer and sends streamed messages through mmMessage messages.
  * Video player in mPatrol connects to local mBox using peering network.
  * Video player in mOffice sends mmPlay request to remote mBox through mServer.
  */

import mmodModule from 'mmodModule'

export default class mmodPlayer extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: { type: 'object', default: { type: 'full' }, description: 'Initial window setup; same structure as required by `mmWindowSet`' },
      mounts: { type: 'string', default: '^(/Volumes/|[a-zA-Z]:|/media)', description: '' },
      mounts_exclude: { type: 'string', default: '^/Volumes/Recovery$', description: 'regexp for excluding volume mounts for export' },
      mounts_extra: { type: 'array', item: { type: 'string' }, description: 'additional extra mounts' },
      filter: { type: 'string', description: 'name of streaming filter or JSON of filter', default: 'player' },
      controls: { type: 'boolean', description: 'setup of mele-player controls setting' },
      timeline: { type: 'boolean', description: 'setup of mele-player timeline setting' },
      mock: { type: 'boolean', description: 'setup of mele-player mock setting' },
      mock_filter: { type: 'string', description: 'setup of mele-player mock_filter setting' },
      mock_h264_shift: { type: 'integer', description: 'setup of mele-player mock_h264_shift setting' },
      marker: { type: 'string', description: 'setup of mele-player marker setting' },
      speed: { type: 'number', description: 'speed for mockup', default: 1 },
      selected: { type: 'integer', description: 'initially selected camera in mele-player', default: 1 },
      init_datetime: { type: 'string', description: 'initial datetime in form YYYY-MM-DD hh:mm:ss Z; if not provided, current time is used' },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  async initWindow() {
    const player = {
      view: true,
      tag: 'mele-player',
      listener_id: this.listener_id+'_player',
      autoplay: true,
      selected: this.config.selected || 1,
      mounts: this.config.mounts,
      mounts_exclude: this.config.mounts_exclude,
      mounts_extra: this.config.mounts_extra,
      filter: this.config.filter,
    }
    if(typeof this.config.controls === 'boolean') {
      player.controls = this.config.controls
    }
    if(typeof this.config.timeline === 'boolean') {
      player.timeline = this.config.timeline
    }
    if(typeof this.config.mock === 'boolean') {
      player.mock = this.config.mock
    }
    if(typeof this.config.speed === 'number') {
      player.speed = this.config.speed
    }
    if(typeof this.config.mock_filter === 'string') {
      player.mock_filter = this.config.mock_filter
    }
    if(typeof this.config.mock_h264_shift === 'number') {
      player.mock_h264_shift = this.config.mock_h264_shift
    }
    if(typeof this.config.marker === 'string') {
      player.marker = this.config.marker
    }
    this.loadForm({
      layout: 'single',
      views: 'player',
      items: [player],
    })
    this.elPlayer = this.el.find('mele-player')
    this.elPlayer.routing = JSON.stringify({
      mmGps: ['speed'],
      mmH264Meta: ['zoom'],
      mmDopplerMeta: [],
      mmDopplerEvent: [],
      mmMark: ['marked'],
      mmMarked: ['marked'],
      mmMarkStop: ['marked'],
    })

    this.mmStart()
    this.subscribe('#WM')

    if(this.config.init_datetime) {
      this.elPlayer.play(new Date(this.config.init_datetime).valueOf()*1000)
    }
  } // initWindow
} // mmodPlayer
